import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Room {
  id: string;
  name: string;
  players: number;
  maxPlayers: number;
  status: 'waiting' | 'playing' | 'full';
  gameType: string;
  createdAt: string;
}

interface RoomsGridProps {
  rooms: Room[];
  loading?: boolean;
  onJoinRoom?: (roomId: string) => void;
  onRefresh?: () => void;
}

const RoomsGrid: React.FC<RoomsGridProps> = ({ rooms, loading, onJoinRoom, onRefresh }) => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('roomsCollapsed') === '1';
  });
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<number>(0);

  useEffect(() => {
    if (!contentRef.current) return;
    const measure = () => setMaxHeight(contentRef.current!.scrollHeight);
    // Measure after paint
    requestAnimationFrame(measure);
    // Re-measure on window resize
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [rooms, loading]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('roomsCollapsed', collapsed ? '1' : '0');
    }
  }, [collapsed]);

  const toggleCollapsed = () => setCollapsed((c) => !c);

  const handleJoinRoom = (roomId: string) => {
    if (onJoinRoom) {
      onJoinRoom(roomId);
    } else {
      navigate(`/game/${roomId}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'waiting': return 'status-waiting';
      case 'playing': return 'status-playing';
      case 'full': return 'status-full';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="rooms-grid">
        <div className="rooms-header">
          <h2 className="rooms-title">Available Rooms</h2>
          {onRefresh && (
            <button className="rooms-refresh" onClick={onRefresh} aria-label="Refresh rooms">
              🔄
            </button>
          )}
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading rooms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rooms-grid">
      <div className="rooms-header">
        <h2 className="rooms-title">Available Rooms</h2>
        <div className="header-actions">
          {onRefresh && (
            <button className="header-icon-btn" onClick={onRefresh} aria-label="Refresh rooms">🔄</button>
          )}
          <button
            className={`header-icon-btn chevron ${collapsed ? 'is-collapsed' : ''}`}
            aria-label={collapsed ? 'Expand rooms' : 'Collapse rooms'}
            aria-expanded={!collapsed}
            onClick={toggleCollapsed}
          >
            ▼
          </button>
        </div>
      </div>

      <div
        ref={contentRef}
        className="collapsible"
        style={{ maxHeight: collapsed ? 0 : maxHeight }}
        aria-hidden={collapsed}
      >
      {rooms.length === 0 ? (
        <div className="rooms-empty empty-state glass-card">
          <div className="empty-icon">🎮</div>
          <h3>No Active Rooms</h3>
          <p>Be the first to create a room and start playing!</p>
          <button 
            className="create-room-button"
            onClick={() => navigate('/rooms/create')}
          >
            ➕ Create Room
          </button>
        </div>
      ) : (
        <div className="rooms-grid-container">
          {rooms.map((room, index) => (
            <div key={room.id || `room-${index}`} className="room-card glass-card">
              <div className="room-header">
                <h3 className="room-name">{room.name}</h3>
                <span className={`room-status ${getStatusColor(room.status)}`}>
                  {room.status}
                </span>
              </div>
              
              <div className="room-info">
                <div className="info-row">
                  <span className="info-icon">👥</span>
                  <span className="info-text">
                    {room.players}/{room.maxPlayers} players
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-icon">🎴</span>
                  <span className="info-text">{room.gameType}</span>
                </div>
              </div>

              <button
                className={`join-button ${room.status === 'waiting' ? 'primary' : 'disabled'}`}
                onClick={() => handleJoinRoom(room.id)}
                disabled={room.status !== 'waiting'}
              >
                {room.status === 'waiting' ? 'Join Room' : 
                 room.status === 'playing' ? 'In Progress' : 'Room Full'}
              </button>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default RoomsGrid;
